import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import LoadingModal from './LoadingModal';
import TableContainer from '../TableContainer';
import Pagination10 from '../Pagination10';
import useDefaultModal from '../hooks/useDefaultModal';
import useConfirmModal from '../hooks/useConfirmModal';
import ConfirmModal from './ConfirmModal';
import DefaultModal from './DefaultModal';
import ResponsiveShadow from '../ResponsiveShadow';
import usePagination10 from '../hooks/usePagination10';
import useParametrosEntidade from '../hooks/useParametrosEntidade';
import PageHeader from '../PageHeader';
import PageContent from '../PageContent';
import ArpOrdemModal from './ArpOrdemModal';
import DataTable from '../DataTable';
import PaginationBar from '../PaginationBar';
import ArpOrdemItensModal from './ArpOrdemItensModal';
import ConfirmarEntregaModal from './ConfirmarEntregaModal';
import ReverterEntregaModal from './ReverterEntregaModal';
import PreEmpenhoModal from './PreEmpenhoModal';
import { Dropdown } from 'react-bootstrap';
import useAsyncPdf from '../hooks/useAsyncPdf';

function ArpOrdensModal({ isOpen, onClose, arp, possuiAditivo }) {
  const [arpOrdens, setArpOrdens] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [atualizar, setAtualizar] = useState(false);
  const [arpOrdemSelecionada, setArpOrdemSelecionada] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user'));
  const isAdmin = user?.role === 'ADMIN';
  const [isLoading, setLoading] = useState(true);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [modalItensAberto, setModalItensAberto] = useState(false);
  const [arpOrdemItemSelecionada, setArpOrdemItemSelecionada] = useState(null);
  const [showEntregaModal, setShowEntregaModal] = useState(false);
  const [showReversaoModal, setShowReversaoModal] = useState(false);
  const [isReversando, setReversando] = useState(false);
  const [reversaoItens, setReversaoItens] = useState([]);
  const [confirmacaoItens, setConfirmacaoItens] = useState([]);
  const [nfData, setNfData] = useState({ numNotaFiscal: '', serieNotaFiscal: '', dataNotaFiscal: '', valorNotaFiscal: '' });
  const [showPreEmpenhoModal, setShowPreEmpenhoModal] = useState(false);
  const [preEmpenhoData, setPreEmpenhoData] = useState(null);
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const generatePdf = useAsyncPdf({ token });
  const { params: paramsEntidade } = useParametrosEntidade(arp?.idEntidade);

  useEffect(() => {
    if (arp && arp.id) {
      carregarOrdens(arp.id);
    }
  }, [arp, atualizar]);

  function carregarOrdens(idArp) {
    setLoading(true);
    axios
      .get(`${API_BASE_URL.ARP_ORDEM}/arp/${idArp}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(result => setArpOrdens(result.data))
      .catch(error => {
        console.error('Erro ao obter ordens da ARP:', error);
        showDefaultModal('error', 'Erro ao carregar ordens da ARP.');
      })
      .finally(() => setLoading(false));
  }

  function handleDelete(arpOrdem) {
    const ordensPosteriores = arpOrdens.filter(o => o.id > arpOrdem.id);
    if (ordensPosteriores.length > 0) {
      showDefaultModal('error', 'Não é possível excluir esta ordem pois já existem ordens posteriores.');
      return;
    }
    if (arpOrdem.status && arpOrdem.status !== 'PENDENTE') {
      showDefaultModal('error', 'Não é possível excluir esta ordem pois já possui entrega registrada.');
      return;
    }
    showConfirmModal('delete', () => handleConfirmDelete(arpOrdem));
  }

  function handleConfirmDelete(arpOrdem) {
    axios
      .delete(`${API_BASE_URL.ARP_ORDEM}/${arp.id}/${arpOrdem.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        showDefaultModal('success', 'Ordem excluída com sucesso!');
        setAtualizar(prev => !prev);
      })
      .catch(error => {
        console.error('Erro ao excluir ordem:', error);
        showDefaultModal('error', 'Erro ao excluir a ordem!');
      });
  }

  function handleEdit(arpOrdem) {
    setArpOrdemSelecionada(arpOrdem);
    setShowModal(true);
  }

  function handleNew() {
    let dataUltimaOrdem = null;
    if (arpOrdens.length > 0) {
      const ordensOrdenadas = [...arpOrdens].sort((a, b) => new Date(a.data) - new Date(b.data));
      const ultimaOrdem = ordensOrdenadas[ordensOrdenadas.length - 1];
      dataUltimaOrdem = ultimaOrdem.data;
      if (!ultimaOrdem.itens || ultimaOrdem.itens.length === 0) {
        showDefaultModal('error', `A última ordem (nº ${ultimaOrdem.id}) não possui itens registrados. Registre os itens antes de criar uma nova ordem.`);
        return;
      }
    }
    setArpOrdemSelecionada({ dataUltimaOrdem });
    setShowModal(true);
  }

  function getBadgeStatus(status) {
    const map = {
      PENDENTE: 'bg-warning text-dark',
      ENTREGUE_PARCIAL: 'bg-info text-dark',
      ENTREGUE_TOTAL: 'bg-success',
      CANCELADO: 'bg-danger',
    };
    return map[status] || 'bg-secondary';
  }

  function handleAbrirEntrega(ordem) {
    if (!ordem) return;
    const itensConf = (ordem.itens || []).map(item => ({
      idArpItem: item.idArpItem,
      descricao: item.arpItem?.descricao || `Item ${item.idArpItem}`,
      unidade: item.arpItem?.unidade || '',
      valorUnit: item.arpItem?.valorUnit || 0,
      qtdePedida: item.qtdePedida || 0,
      qtdeEntregueAnt: item.qtdeEntregue || 0,
      qtdeEntregueNova: 0,
    }));
    setConfirmacaoItens(itensConf);
    setNfData({ numNotaFiscal: '', serieNotaFiscal: '', dataNotaFiscal: '', valorNotaFiscal: '' });
    setShowEntregaModal(true);
  }

  function handleConfirmarEntrega() {
    const payload = {
      dataConfirmacao: new Date().toISOString().split('T')[0],
      itens: confirmacaoItens.map(i => ({
        idArpItem: i.idArpItem,
        qtdeEntregue: i.qtdeEntregueNova,
      })),
      ...nfData.numNotaFiscal && {
        numNotaFiscal: nfData.numNotaFiscal,
        serieNotaFiscal: nfData.serieNotaFiscal,
        dataNotaFiscal: nfData.dataNotaFiscal || null,
        valorNotaFiscal: parseFloat(nfData.valorNotaFiscal.replace(/\./g, '').replace(',', '.')) || 0,
      },
    };

    axios
      .put(
        `${API_BASE_URL.ARP_ORDEM}/${arp.id}/${arpOrdemSelecionada.id}/confirmar`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(() => {
        setShowEntregaModal(false);
        showDefaultModal('success', 'Entrega confirmada com sucesso!');
        setAtualizar(prev => !prev);
        setArpOrdemSelecionada(null);
      })
      .catch(error => {
        console.error('Erro ao confirmar entrega:', error);
        showDefaultModal('error', 'Erro ao confirmar entrega!');
      });
  }

  function handleItemChange(idx, val) {
    setConfirmacaoItens(prev =>
      prev.map((it, i) => i === idx ? { ...it, qtdeEntregueNova: val } : it)
    );
  }

  function handleConfirmarEntregaTotal() {
    const itensTotal = confirmacaoItens.map(item => ({
      ...item,
      qtdeEntregueNova: item.qtdePedida - item.qtdeEntregueAnt,
    }));
    setConfirmacaoItens(itensTotal);
    const payload = {
      dataConfirmacao: new Date().toISOString().split('T')[0],
      itens: itensTotal.map(i => ({
        idArpItem: i.idArpItem,
        qtdeEntregue: i.qtdeEntregueNova,
      })),
      ...nfData.numNotaFiscal && {
        numNotaFiscal: nfData.numNotaFiscal,
        serieNotaFiscal: nfData.serieNotaFiscal,
        dataNotaFiscal: nfData.dataNotaFiscal || null,
        valorNotaFiscal: parseFloat(nfData.valorNotaFiscal.replace(/\./g, '').replace(',', '.')) || 0,
      },
    };
    axios
      .put(
        `${API_BASE_URL.ARP_ORDEM}/${arp.id}/${arpOrdemSelecionada.id}/confirmar`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(() => {
        setShowEntregaModal(false);
        showDefaultModal('success', 'Entrega total confirmada com sucesso!');
        setAtualizar(prev => !prev);
        setArpOrdemSelecionada(null);
      })
      .catch(error => {
        console.error('Erro ao confirmar entrega total:', error);
        showDefaultModal('error', 'Erro ao confirmar entrega total!');
      });
  }

  function handleAbrirReversao(ordem) {
    if (!ordem) return;
    const itensRev = (ordem.itens || []).map(item => ({
      idItem: item.idArpItem,
      descricao: item.arpItem?.descricao || `Item ${item.idArpItem}`,
      unidade: item.arpItem?.unidade || '',
      qtdePedida: item.qtdePedida || 0,
      qtdeEntregue: item.qtdeEntregue || 0,
    }));
    setReversaoItens(itensRev);
    setShowReversaoModal(true);
  }

  function handleReverter(payload) {
    setReversando(true);
    axios
      .put(
        `${API_BASE_URL.ARP_ORDEM}/${arp.id}/${arpOrdemSelecionada.id}/reverter`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(() => {
        setShowReversaoModal(false);
        showDefaultModal('success', 'Entrega revertida com sucesso!');
        setAtualizar(prev => !prev);
        setArpOrdemSelecionada(null);
      })
      .catch(error => {
        console.error('Erro ao reverter entrega:', error);
        showDefaultModal('error', error.response?.data?.error || 'Erro ao reverter a entrega!');
      })
      .finally(() => setReversando(false));
  }

  function handleDownloadPdf(ordem) {
    if (!arp || !ordem) {
      showDefaultModal('error', 'Nenhuma ordem selecionada.');
      return;
    }
    const ordemDTO = {
      id: ordem.id,
      idArp: arp.id,
      data: ordem.data,
      idProprio: ordem.idProprio || null,
      usuario: ordem.usuario,
      itens: ordem.itens || [],
    };
    setIsGeneratingPdf(true);
    generatePdf({
      startFn: () => axios.post(`${API_BASE_URL.ARP_ORDEM}/preencherOrdemPdf`, ordemDTO, { headers: { Authorization: `Bearer ${token}` } }),
      statusUrlFn: jobId => `${API_BASE_URL.ARP_ORDEM}/preencherOrdemPdfStatus/${jobId}`,
      fileUrlFn: jobId => `${API_BASE_URL.ARP_ORDEM}/preencherOrdemPdfFile/${jobId}`,
    })
      .then(({ blob }) => {
        const url = window.URL.createObjectURL(blob);
        const pdfId = `ordem_${ordemDTO.idArp}_${ordemDTO.id}_${Date.now()}`;
        if (!window.pdfStorage) window.pdfStorage = {};
        window.pdfStorage[pdfId] = blob;
        sessionStorage.setItem('pdfViewerData', JSON.stringify({ url, title: `Ordem ${ordem.idProprio || ordemDTO.id} - ARP ${arp.numArp}`, pdfId }));
        window.dispatchEvent(new CustomEvent('pdfViewerUpdate', { detail: { pdfId } }));
      })
      .catch(error => {
        console.error('Erro ao gerar PDF:', error);
        showDefaultModal('error', error.message || 'Erro ao gerar o PDF da ordem.');
      })
      .finally(() => setIsGeneratingPdf(false));
  }

  function handleDownloadOrdemWord(ordem) {
    if (!arp || !ordem) {
      showDefaultModal('error', 'Nenhuma ordem selecionada.');
      return;
    }
    const ordemDTO = {
      id: ordem.id,
      idArp: arp.id,
      data: ordem.data,
      idProprio: ordem.idProprio || null,
      usuario: ordem.usuario,
      itens: ordem.itens || [],
    };
    setIsGeneratingPdf(true);
    axios
      .post(`${API_BASE_URL.ARP_ORDEM}/preencherOrdemDocx`, ordemDTO, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      })
      .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = `ORDEM_${ordem.id}.docx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(error => {
        console.error('Erro ao baixar Word:', error);
        showDefaultModal('error', 'Erro ao baixar o documento Word da ordem.');
      })
      .finally(() => setIsGeneratingPdf(false));
  }

  function handleDpePdf() {
    setIsGeneratingPdf(true);
    axios
      .get(`${API_BASE_URL.PRE_EMPENHO}/por-vinculo`, {
        params: { tipoVinculo: 'ORDEM', idVinculo: arp.id, idVinculo2: arpOrdemSelecionada.id },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(res => {
        if (res.data && res.data.id) {
          return openPreEmpenhoPdf(res.data.id, res.data.numPreEmpenho);
        } else {
          showDefaultModal('error', 'Nenhum Pré-Empenho encontrado para esta ordem.');
        }
      })
      .catch(() => showDefaultModal('error', 'Nenhum Pré-Empenho encontrado para esta ordem.'))
      .finally(() => setIsGeneratingPdf(false));
  }

  function handleDpeWord() {
    setIsGeneratingPdf(true);
    axios
      .get(`${API_BASE_URL.PRE_EMPENHO}/por-vinculo`, {
        params: { tipoVinculo: 'ORDEM', idVinculo: arp.id, idVinculo2: arpOrdemSelecionada.id },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(res => {
        if (res.data && res.data.id) {
          return openPreEmpenhoWord(res.data.id);
        } else {
          showDefaultModal('error', 'Nenhum Pré-Empenho encontrado para esta ordem.');
        }
      })
      .catch(() => showDefaultModal('error', 'Nenhum Pré-Empenho encontrado para esta ordem.'))
      .finally(() => setIsGeneratingPdf(false));
  }

  function openPreEmpenhoPdf(id, num) {
    return generatePdf({
      startFn: () => axios.get(`${API_BASE_URL.PRE_EMPENHO}/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } }),
      statusUrlFn: jobId => `${API_BASE_URL.PRE_EMPENHO}/pdfStatus/${jobId}`,
      fileUrlFn: jobId => `${API_BASE_URL.PRE_EMPENHO}/pdfFile/${jobId}`,
    })
      .then(({ blob, missingFields }) => {
        const url = window.URL.createObjectURL(blob);
        const pdfId = `pre_empenho_${id}_${Date.now()}`;
        if (!window.pdfStorage) window.pdfStorage = {};
        window.pdfStorage[pdfId] = blob;
        if (missingFields && missingFields.length > 0) {
          showDefaultModal('warning', missingFields.join('\n'), () => {
            sessionStorage.setItem('pdfViewerData', JSON.stringify({ url, title: `Pré-Empenho ${num || id}`, pdfId }));
            window.dispatchEvent(new CustomEvent('pdfViewerUpdate', { detail: { pdfId } }));
          });
        } else {
          sessionStorage.setItem('pdfViewerData', JSON.stringify({ url, title: `Pré-Empenho ${num || id}`, pdfId }));
          window.dispatchEvent(new CustomEvent('pdfViewerUpdate', { detail: { pdfId } }));
        }
      })
      .catch(err => {
        console.error('Erro ao gerar PDF do Pré-Empenho:', err);
        showDefaultModal('error', err.message || 'Erro ao gerar o PDF do Pré-Empenho.');
      });
  }

  function openPreEmpenhoWord(id) {
    return axios
      .get(`${API_BASE_URL.PRE_EMPENHO}/${id}/docx`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const doDownload = () => {
          const link = document.createElement('a');
          link.href = url;
          link.download = `PRE_EMPENHO_${id}.docx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        };
        const missingHeader = res.headers['x-missing-fields'];
        if (missingHeader) {
          const fields = missingHeader.split(',');
          showDefaultModal('warning', fields.join('\n'), doDownload);
        } else {
          doDownload();
        }
      })
      .catch(err => {
        console.error('Erro ao baixar Word do Pré-Empenho:', err);
        showDefaultModal('error', 'Erro ao gerar o documento Word do Pré-Empenho.');
      });
  }

  function parseDateBRtoISO(dateStr) {
    if (!dateStr) return '';
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const [d, m, y] = parts;
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    return dateStr;
  }

  function handleImportJson(event) {
    if (!arp) return;
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      let data;
      try {
        data = JSON.parse(e.target.result);
      } catch {
        showDefaultModal('error', 'Arquivo JSON inválido.');
        event.target.value = '';
        return;
      }
      if (!Array.isArray(data) || data.length === 0) {
        showDefaultModal('error', 'O JSON deve ser um array não vazio.');
        event.target.value = '';
        return;
      }
      setLoading(true);
      let successCount = 0;
      let errorCount = 0;
      for (const obj of data) {
        try {
          const ordemPayload = {
            idArp: arp.id,
            id: obj.ordem.id || null,
            data: parseDateBRtoISO(obj.ordem.data),
            itens: [],
          };
          const ordemResp = await axios.post(API_BASE_URL.ARP_ORDEM, ordemPayload, { headers: { Authorization: `Bearer ${token}` } });
          const ordemId = ordemResp.data?.id || obj.ordem.id;
          if (Array.isArray(obj.itens)) {
            for (const item of obj.itens) {
              const itemPayload = {
                idArp: arp.id,
                idArpOrdem: ordemId,
                idArpItem: item.idArpItem,
                qtdeAcumAnterior: item.qtdeAcumAnterior,
                qtdePedida: item.qtdePedida,
                qtdeAcumAtual: item.qtdeAcumAtual,
                idArpContrato: item.idArpContrato || undefined,
              };
              await axios.post(API_BASE_URL.ARP_ORDEM_ITEM, itemPayload, { headers: { Authorization: `Bearer ${token}` } });
            }
          }
          successCount++;
        } catch (err) {
          console.error('Erro ao importar ordem:', err);
          errorCount++;
        }
      }
      setLoading(false);
      setAtualizar(prev => !prev);
      if (errorCount === 0) {
        showDefaultModal('success', `${successCount} ordem(ns) importada(s) com sucesso!`);
      } else {
        showDefaultModal('error', `${successCount} importada(s) com sucesso, ${errorCount} com erro.`);
      }
      event.target.value = '';
    };
    reader.readAsText(file);
  }

  const normalizeString = str =>
    str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();

  const formatDate = date => {
    if (!date) return '';
    if (Array.isArray(date)) {
      const [year, month, day] = date;
      return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
    }
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  };

  const arpOrdensFiltradas = Array.isArray(arpOrdens)
    ? arpOrdens
        .filter(ordem => {
          const b = normalizeString(filtro);
          return (
            normalizeString(ordem.id?.toString() || '').includes(b) ||
            normalizeString(formatDate(ordem.data) || '').includes(b)
          );
        })
        .sort((a, b) => (b.id || 0) - (a.id || 0))
    : [];

  const totalItems = arpOrdensFiltradas.length;
  const { setPagination } = usePagination10(totalItems);
  const currentItems = arpOrdensFiltradas.slice(setPagination.startIndex, setPagination.startIndex + setPagination.rowsPerPage);

  const calcularValorTotal = (ordem) => {
    if (!ordem.itens || !Array.isArray(ordem.itens)) return 0;
    return ordem.itens.reduce((total, item) => total + ((item.qtdePedida || 0) * (item.arpItem?.valorUnit || 0)), 0);
  };

  const calcularValorEntregue = (ordem) => {
    if (!ordem.itens || !Array.isArray(ordem.itens)) return 0;
    return ordem.itens.reduce((total, item) => total + ((item.qtdeEntregue || 0) * (item.arpItem?.valorUnit || 0)), 0);
  };

  const calcularTotalGeral = () =>
    arpOrdensFiltradas.reduce((total, ordem) => total + calcularValorTotal(ordem), 0);

  const calcularTotalGeralEntregue = () =>
    arpOrdensFiltradas.reduce((total, ordem) => total + calcularValorEntregue(ordem), 0);

  const calcularTotalGeralPendente = () =>
    calcularTotalGeral() - calcularTotalGeralEntregue();

  const possuiOrdemPosterior = (ordem) => {
    if (!ordem) return false;
    return arpOrdens.some(o => o.id > ordem.id);
  };

  const possuiEntrega = (ordem) => {
    if (!ordem) return false;
    return ordem.status && ordem.status !== 'PENDENTE';
  };

  function handleCloseModal() {
    setShowModal(false);
    setArpOrdemSelecionada(null);
  }

  function handleSaveSuccess(novaOrdem) {
    setShowModal(false);
    setArpOrdemSelecionada(null);
    setAtualizar(prev => !prev);
    if (novaOrdem?.id) {
      setArpOrdemItemSelecionada(novaOrdem);
      setModalItensAberto(true);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)'}}>
      <div className="modal-dialog modal-lg" style={{ maxWidth: '95vw', height: '90vh' }}>
          <div className="modal-content h-100 overflow-hidden">
          <div
            className="modal-header text-white"
            style={{
              background: 'linear-gradient(45deg, rgb(7, 29, 65) 0%, rgba(29, 100, 216, 1) 100%)',
              padding: '0.5rem 1rem',
              borderTopLeftRadius: '0.5rem',
              borderTopRightRadius: '0.5rem',
              border: 'none',
            }}
          >
            <h6 className="modal-title mb-0">
              <i className="bi bi-cart3 me-2"></i>
              Ordens de Fornecimento da ARP {arp?.numArp}
            </h6>
            <button type="button" className="btn-close btn-close-white" onClick={onClose} title="Fechar"></button>
          </div>
          <PageContent>
            <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-2 mb-2">
              <div className="botao-grid-2col">
                <button className="btn btn-success btn-sm botao-igual" onClick={handleNew} disabled={possuiAditivo} title={possuiAditivo ? 'Bloqueado - ordens são registradas no aditivo' : 'Nova ordem de fornecimento'}>
                  <i className="bi bi-plus-circle me-1"></i>Nova
                </button>
                <button
                  className="btn btn-primary btn-sm botao-igual"
                  disabled={!arpOrdemSelecionada || possuiAditivo}
                  onClick={() => handleEdit(arpOrdemSelecionada)}
                  title={possuiAditivo ? 'Bloqueado - ordens são registradas no aditivo' : (!arpOrdemSelecionada ? 'Selecione uma ordem' : 'Editar ordem selecionada')}
                >
                  <i className="bi bi-pencil me-1"></i>Editar
                </button>
                {paramsEntidade.EDITAR_DPE !== 'N' && (
                  <button
                    className="btn btn-primary btn-sm botao-igual"
                    disabled={!arpOrdemSelecionada || possuiAditivo}
                    onClick={() => {
                      axios
                        .get(`${API_BASE_URL.PRE_EMPENHO}/por-vinculo`, {
                          params: { tipoVinculo: 'ORDEM', idVinculo: arp.id, idVinculo2: arpOrdemSelecionada.id },
                          headers: { Authorization: `Bearer ${token}` },
                        })
                        .then(res => {
                          if (res.data && res.data.id) {
                            setPreEmpenhoData(res.data);
                          } else {
                            setPreEmpenhoData({ objeto: arp?.objeto || '' });
                          }
                          setShowPreEmpenhoModal(true);
                        })
                        .catch(() => {
                          setPreEmpenhoData({ objeto: arp?.objeto || '' });
                          setShowPreEmpenhoModal(true);
                        });
                    }}
                    title="Editar Pré-Empenho da ordem selecionada"
                  >
                    <i className="bi bi-pencil me-1"></i>
                    Editar DPE
                  </button>
                )}
                <button
                  className="btn btn-danger btn-sm botao-igual"
                  disabled={!arpOrdemSelecionada || possuiAditivo || possuiOrdemPosterior(arpOrdemSelecionada) || possuiEntrega(arpOrdemSelecionada)}
                  onClick={() => handleDelete(arpOrdemSelecionada)}
                  title={
                    possuiAditivo
                      ? 'Bloqueado - ordens são registradas no aditivo'
                      : !arpOrdemSelecionada
                      ? 'Selecione uma ordem'
                      : possuiOrdemPosterior(arpOrdemSelecionada)
                      ? 'Ordem bloqueada - existem ordens posteriores'
                      : possuiEntrega(arpOrdemSelecionada)
                      ? 'Ordem bloqueada - já possui entrega registrada'
                      : 'Excluir ordem selecionada'
                  }
                >
                  <i className="bi bi-trash me-1"></i>Excluir
                </button>
                <div className="toolbar-separator" />
              <button
                    className="btn btn-success btn-sm text-white botao-igual"
                    disabled={!arpOrdemSelecionada}
                    onClick={() => { setArpOrdemItemSelecionada(arpOrdemSelecionada); setModalItensAberto(true); }}
                    title="Ver itens da ordem selecionada"
                  >
                    <i className="bi bi-list-check me-1"></i>Itens da Ordem
                  </button>
                <Dropdown>
                  <Dropdown.Toggle variant="secondary" size="sm" disabled={!arpOrdemSelecionada} className="botao-igual">
                    <i className="bi bi-eye me-1"></i>Visualizar
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <div className="px-3 py-1 d-flex align-items-center justify-content-between gap-3" style={{ minWidth: 200 }}>
                      <span className="small fw-semibold">Ordem</span>
                      <div className="d-flex gap-1">
                        {paramsEntidade.VISUALIZAR_TIPO !== 'WORD' && (
                          <button className="btn btn-sm btn-outline-danger px-1 py-0" onClick={e => { e.stopPropagation(); handleDownloadPdf(arpOrdemSelecionada); }} title="Abrir PDF da Ordem">
                            <i className="bi bi-file-pdf"></i>
                          </button>
                        )}
                        {paramsEntidade.VISUALIZAR_TIPO !== 'PDF' && (
                          <button className="btn btn-sm btn-outline-primary px-1 py-0" onClick={e => { e.stopPropagation(); handleDownloadOrdemWord(arpOrdemSelecionada); }} title="Baixar Word da Ordem">
                            <i className="bi bi-file-word"></i>
                          </button>
                        )}
                      </div>
                    </div>
                    <Dropdown.Divider className="my-1" />
                    <div className="px-3 py-1 d-flex align-items-center justify-content-between gap-3" style={{ minWidth: 200 }}>
                      <span className="small fw-semibold">DPE</span>
                      <div className="d-flex gap-1">
                        {paramsEntidade.VISUALIZAR_TIPO !== 'WORD' && (
                          <button className="btn btn-sm btn-outline-danger px-1 py-0" onClick={e => { e.stopPropagation(); handleDpePdf(); }} title="Abrir PDF do DPE">
                            <i className="bi bi-file-pdf"></i>
                          </button>
                        )}
                        {paramsEntidade.VISUALIZAR_TIPO !== 'PDF' && (
                          <button className="btn btn-sm btn-outline-primary px-1 py-0" onClick={e => { e.stopPropagation(); handleDpeWord(); }} title="Baixar Word do DPE">
                            <i className="bi bi-file-word"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </Dropdown.Menu>
                </Dropdown>
                <button
                  className="btn btn-success btn-sm botao-igual"
                  disabled={!arpOrdemSelecionada}
                  onClick={() => handleAbrirEntrega(arpOrdemSelecionada)}
                  title="Visualizar entregas da ordem selecionada"
                >
                  <i className="bi bi-check2-circle me-1"></i>Entregas
                </button>
                {paramsEntidade.REVERTER_ENTREGA === 'S' && (
                  <button
                    className="btn btn-danger btn-sm botao-igual"
                    disabled={!arpOrdemSelecionada || !possuiEntrega(arpOrdemSelecionada)}
                    onClick={() => handleAbrirReversao(arpOrdemSelecionada)}
                    title={
                      !arpOrdemSelecionada
                        ? 'Selecione uma ordem'
                        : !possuiEntrega(arpOrdemSelecionada)
                        ? 'Ordem sem entrega registrada'
                        : 'Reverter entrega da ordem selecionada'
                    }
                  >
                    <i className="bi bi-arrow-counterclockwise me-1"></i>Reverter
                  </button>
                )}
                {isAdmin && (
                  <label className="btn btn-secondary btn-sm botao-igual" title={possuiAditivo ? 'Bloqueado - ordens são registradas no aditivo' : 'Importar ordens via JSON'} style={{ cursor: possuiAditivo ? 'not-allowed' : 'pointer' }}>
                    <i className="bi bi-file-earmark-arrow-up me-1"></i>Import JSON
                    <input
                      type="file"
                      accept=".json,application/json"
                      style={{ display: 'none' }}
                      onChange={handleImportJson}
                      disabled={possuiAditivo}
                    />
                  </label>
                )}
              </div>
              <div className="col-12 col-lg-2 ms-lg-auto">
                <div className="input-group input-group-sm">
                  <span className="input-group-text"><i className="bi bi-search"></i></span>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Buscar..."
                    title="Buscar por número da ordem ou data"
                    value={filtro}
                    onChange={e => setFiltro(e.target.value.toUpperCase())}
                  />
                </div>
              </div>
            </div>

            <LoadingModal loading={isGeneratingPdf} message="Gerando PDF, por favor aguarde..." />
            <TableContainer isLoading={isLoading}>
              <DataTable style={{ fontSize: '0.8rem' }} head={
                <tr>
                  <th scope="col" className="px-2">Nº Ordem</th>
                  <th scope="col" className="px-2">Data</th>
                  <th scope="col" className="px-2 text-center">Status</th>
                  <th scope="col" className="px-2 text-end">Qtd. Itens</th>
                  <th scope="col" className="px-2 text-end">Valor Total Solicitado</th>
                  <th scope="col" className="px-2 text-end">Valor Entregue</th>
                  <th scope="col" className="px-2 text-end">Valor Pendente</th>
                </tr>}>
                <tbody>
                  {currentItems.map(ordem => {
                      const bloqueado = possuiOrdemPosterior(ordem);
                      return (
                        <tr
                          key={ordem.id}
                          className={arpOrdemSelecionada && arpOrdemSelecionada.id === ordem.id ? 'table-primary' : ''}
                          style={{ cursor: 'pointer' }}
                          onClick={() => setArpOrdemSelecionada(ordem)}
                        >
                          <td className="px-2">
                            {ordem.idProprio || ordem.id}
                            {bloqueado && (
                              <i className="bi bi-lock-fill ms-2 text-warning" title="Ordem bloqueada - existem ordens posteriores"></i>
                            )}
                            {!bloqueado && possuiEntrega(ordem) && (
                              <i className="bi bi-lock-fill ms-2 text-danger" title="Ordem bloqueada - já possui entrega registrada"></i>
                            )}
                          </td>
                          <td className="px-2">{formatDate(ordem.data)}</td>
                          <td className="px-2 text-center">
                            <span
                              className={`badge ${getBadgeStatus(ordem.status || 'PENDENTE')}`}
                              title={
                                `Ordem por: ${ordem.usuario || '—'}` +
                                (ordem.usuarioConfirmacao ? `\nConfirmado por: ${ordem.usuarioConfirmacao}` : '')
                              }
                              style={{ cursor: 'help' }}
                            >
                              {ordem.status || 'PENDENTE'}
                            </span>
                          </td>
                          <td className="px-2 text-end">{ordem.itens?.length || 0}</td>
                          <td className="px-2 text-end">
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(calcularValorTotal(ordem))}
                          </td>
                          <td className="px-2 text-end">
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(calcularValorEntregue(ordem))}
                          </td>
                          <td className="px-2 text-end fw-bold text-primary">
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(calcularValorTotal(ordem) - calcularValorEntregue(ordem))}
                          </td>
                        </tr>
                      );
                    })}
                  {currentItems.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center py-3 text-muted">
                        <i className="bi bi-inbox fs-3 d-block mb-2"></i>
                        Nenhuma ordem encontrada
                      </td>
                    </tr>
                  )}
                </tbody>
                {arpOrdensFiltradas.length > 0 && (
                  <tfoot className="fw-bold" style={{ position: 'sticky', bottom: 0, zIndex: 2, backgroundColor: '#f8f9fa', isolation: 'isolate' }}>
                    <tr>
                      <td colSpan="4" className="px-2 text-end">TOTAL GERAL:</td>
                      <td className="px-2 text-end">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(calcularTotalGeral())}
                      </td>
                      <td className="px-2 text-end text-success">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(calcularTotalGeralEntregue())}
                      </td>
                      <td className="px-2 text-end fw-bold text-primary">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(calcularTotalGeralPendente())}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </DataTable>
            </TableContainer>
          </PageContent>
          <PaginationBar setPagination={setPagination} />
        </div>
      </div>

      {showModal && (
        <ArpOrdemModal
          show={showModal}
          arpOrdem={arpOrdemSelecionada?.id ? arpOrdemSelecionada : null}
          arp={arp}
          onClose={handleCloseModal}
          onSave={handleSaveSuccess}
          dataUltimaOrdem={arpOrdemSelecionada && !arpOrdemSelecionada.id ? arpOrdemSelecionada.dataUltimaOrdem : null}
        />
      )}

      <ArpOrdemItensModal
        isOpen={modalItensAberto}
        onClose={() => { setModalItensAberto(false); carregarOrdens(arp.id); }}
        arp={arp}
        arpOrdem={arpOrdemItemSelecionada}
      />
      <PreEmpenhoModal
        show={showPreEmpenhoModal}
        preEmpenho={preEmpenhoData}
        onClose={() => { setShowPreEmpenhoModal(false); setPreEmpenhoData(null); }}
        onSave={() => { setShowPreEmpenhoModal(false); setPreEmpenhoData(null); }}
      />
      <ConfirmModal {...setConfirmModal} />
      <DefaultModal {...setDefaultModal} />

      <ConfirmarEntregaModal
        show={showEntregaModal}
        entityType="Ordem"
        entityId={arpOrdemSelecionada?.id}
        usuario={arpOrdemSelecionada?.usuario}
        itens={confirmacaoItens}
        onItemChange={handleItemChange}
        onClose={() => setShowEntregaModal(false)}
        onConfirm={handleConfirmarEntrega}
        onConfirmTotal={handleConfirmarEntregaTotal}
        numNotaFiscal={nfData.numNotaFiscal}
        serieNotaFiscal={nfData.serieNotaFiscal}
        dataNotaFiscal={nfData.dataNotaFiscal}
        valorNotaFiscal={nfData.valorNotaFiscal}
        onNfChange={({ name, value }) => setNfData(prev => ({ ...prev, [name]: value }))}
      />
      <ReverterEntregaModal
        show={showReversaoModal}
        entityType="Ordem"
        entityId={arpOrdemSelecionada?.id}
        itens={reversaoItens}
        isSaving={isReversando}
        onConfirm={handleReverter}
        onClose={() => setShowReversaoModal(false)}
      />
    </div>
  );
}

export default ArpOrdensModal;
